import { Getter } from '@zajno/common/types';
import { createLazyObjectSuspend } from './lazy-suspend';
import { VideoTexture, TextureLoader, SRGBColorSpace } from 'three';
import type { WebGLRenderer } from 'three';
import { useThree } from '@react-three/fiber';
import { useEffect } from 'react';
import { ILogger } from '@zajno/common/logger';
import { makeObservable, observable, runInAction } from 'mobx';
import { FlagModel } from '@zajno/common-mobx/viewModels';

// copy-paste and adaption from @react-three/drei/core/useVideoTexture.d.ts

const SIMULATE_LOW_BATTERY_MODE = false;

export type VideoSourceArgs = { src: string, type: string }[];
export type SrcSetArgs = string | VideoSourceArgs;

type VideoEventTypes = 'canplay' | 'canplaythrough' | 'loadstart' | 'loadedmetadata';

interface VideoTextureArgs extends Partial<Omit<HTMLVideoElement, 'src' | 'srcObject'>> {
    srcset: Getter<SrcSetArgs | MediaStream>;
    fallbackImageSrc?: Getter<string>;
    colorSpace?: THREE.ColorSpace;
    material?: THREE.MeshBasicMaterial;
    key: string;
    priority?: number;
    unsuspend?: VideoEventTypes;
    start?: boolean;
    logger?: ILogger;
}

export function createLazyVideoTexture(args: VideoTextureArgs) {

    const {
        priority,
        unsuspend,
        start,
        crossOrigin,
        muted,
        loop,
        logger,
        key,
        colorSpace,
        srcset: srcsetGetter,
        fallbackImageSrc,
        material,
        ...rest
    } = {
        unsuspend: 'canplay' as VideoEventTypes,
        crossOrigin: 'Anonymous',
        muted: true,
        loop: true,
        start: true,
        playsInline: true,
        priority: 0,
        colorSpace: SRGBColorSpace,
        ...args,
    };

    const result = createLazyObjectSuspend({
        priority: priority,
        logger,
        cacheKeys: [key],
        preloader: () => {
            return new Promise<VideoTextureResult>((resolve, reject) => {
                const video = document.createElement('video');
                video.crossOrigin = crossOrigin;
                video.muted = muted;
                video.loop = loop;
                Object.assign(video, rest);

                const srcset = Getter.getValue(srcsetGetter);

                logger?.log('Resolved src:', srcset);

                if (srcset instanceof MediaStream) {
                    video.srcObject = srcset;
                } else if (Array.isArray(srcset)) {
                    srcset.forEach((pair) => {
                        const source = document.createElement('source');
                        source.src = pair.src;
                        source.type = pair.type;
                        video.appendChild(source);
                    });
                } else {
                    video.src = srcset;
                }

                const createResult = (withVideo = true) => new VideoTextureResult(withVideo ? video : null, colorSpace)
                    .setFallbackImageSrc(fallbackImageSrc)
                    .setLogger(logger)
                    .setMaterial(material);

                const onUnsuspend = () => {
                    video.removeEventListener('error', reject);
                    video.removeEventListener(unsuspend, onUnsuspend);

                    const onPlayOk = async () => {
                        // set offset non zero for texture to re-render
                        stopVideo(video, 0.001);

                        logger?.log('Video tested for playing, now resolving...');
                        resolve(createResult(true));
                    };
                    const onPlayFail = (err: Error) => {
                        logger?.warn('Video play failed: ', err);
                        resolve(createResult(false));
                        video.remove();
                    };

                    video.play()
                        .then(!SIMULATE_LOW_BATTERY_MODE
                            ? onPlayOk
                            : () => onPlayFail(new Error('Fake SIMULATE_LOW_BATTERY_MODE error (video play failed)')),
                        )
                        .catch(onPlayFail);
                };

                video.addEventListener('error', reject);
                video.addEventListener(unsuspend, onUnsuspend);
            });
        },

        usePreHook: () => {
            const gl: WebGLRenderer = useThree(state => state.gl);
            return { gl };
        },

        loader: async (result, pre) => {
            const { gl: { outputColorSpace: colorSpace } } = pre;

            logger?.log('creating video texture...');

            await result.initVideoTexture(colorSpace);
            return result;
        },

        usePostHook: (result) => {
            useEffect(() => {
                if (start) {
                    result.safePlay();
                }
            }, [result]);
        },
    });

    return {
        ...result,
    };
}

class VideoTextureResult {

    private _video: HTMLVideoElement = null;
    private _texture: THREE.Texture = null;
    private _material: THREE.MeshBasicMaterial = null;

    private readonly _isPlaceholder = new FlagModel(false);
    private _placeholderImageSrc: Getter<string>;
    private logger: ILogger;

    private _colorSpace: THREE.ColorSpace = null;

    constructor(video: HTMLVideoElement, colorSpace?: THREE.ColorSpace) {
        this._video = video;
        this._colorSpace = colorSpace;

        makeObservable<VideoTextureResult, '_texture'>(this, {
            _texture: observable.ref,
        });
    }

    public get texture() { return this._texture; }
    public get isPlaceholder() { return this._isPlaceholder.value; }

    public setFallbackImageSrc(fallback: Getter<string>) {
        this._placeholderImageSrc = fallback;
        return this;
    }

    public setMaterial(material: THREE.MeshBasicMaterial) {
        this._material = material;
        return this;
    }

    public setLogger(logger: ILogger) {
        this.logger = logger;
        return this;
    }

    public async initVideoTexture(colorSpace: THREE.ColorSpace) {
        this._colorSpace = colorSpace;

        if (!this._video) {
            await this.replaceTexture();
            return;
        }

        const texture = new VideoTexture(this._video);
        texture.colorSpace = this._colorSpace;

        this._setTexture(texture);
    }

    public safePlay() {
        if (this._isPlaceholder.value || !this._video) {
            return;
        }

        this._video.play().catch(err => {
            this.logger?.warn('Video play failed: ', err);
            this.replaceTexture();
        });
    }

    public stop() {
        stopVideo(this._video);
    }

    private async replaceTexture() {
        const fallback = Getter.getValue(this._placeholderImageSrc);
        if (fallback && !this._isPlaceholder.value) {
            this._isPlaceholder.setTrue();

            this.logger?.log('Replacing video texture with fallback image:', fallback);

            const texture = await new TextureLoader().loadAsync(fallback);

            texture.colorSpace = this._colorSpace;

            this._video?.remove();
            this._video = null;
            this._setTexture(texture);
        }
    }

    private _setTexture(texture: THREE.Texture) {
        runInAction(() => {
            this._texture = texture;

            if (this._material) {
                this._material.map = texture;
                this._material.needsUpdate = true;

                this.logger?.log('VideoTextureResult: overriding material map');
            }
        });
    }
}

function stopVideo(video: HTMLVideoElement, offset = 0) {
    if (!video) {
        return;
    }

    video.pause();
    video.currentTime = offset;
}
